import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Dropdown, List } from 'tea-component';
import { arrayToSimpleObj, getStartAndEndValue } from '../../utils';
import { useIntl } from 'umi';
import { LocaleType } from '@/pages/opinion/types/common';
import type { PlaceholderType } from '../../types';
import classNames from 'classnames';
import '../scss/range.scss';
import './index.scss';
import { addFilterBuriedPoint } from '../../utils/point';
import { AllFilterPointType } from '@op/operationLog/pages/filter';
import CustomInput from '../CustomInput';

function CustomFollowerRange(
  {
    lang,
    options,
    rangeValue,
    setRangeValue,
    placeholder,
    hasCustomize = true,
    unit = 'K',
    unitPosition = 'suffix',
    maxValue,
    minValue,
    maxErrorText,
    minErrorText,
    errorTextFormatted = false,
    regex,
    emptyErrorText,
    valueErrorText,
    backText,
    pointKey,
    getBackPoint,
    className
  }: {
    /** 当前语言环境 */
    lang: LocaleType;
    /** 下拉选项列表，每项包含显示文本和值 */
    options: Array<{ text: string; value: string }>;
    /** 当前选中的范围值 */
    rangeValue: string;
    /** 设置范围值的回调 */
    setRangeValue: (value: string) => void;
    /** 各语言下的占位符文本 */
    placeholder: PlaceholderType;
    /** 是否显示自定义输入入口，默认 true */
    hasCustomize?: boolean;
    /** 单位符号（如 K、M），默认 'K' */
    unit?: string;
    /** 单位位置：'suffix' 后缀（默认） | 'prefix' 前缀 */
    unitPosition?: 'prefix' | 'suffix';
    /** 最大值限制 */
    maxValue?: number;
    /** 最小值限制 */
    minValue?: number;
    /** 超过最大值时的自定义错误提示文案 */
    maxErrorText?: string;
    /** 低于最小值时的自定义错误提示文案 */
    minErrorText?: string;
    /** 标记 maxErrorText / minErrorText 是否已经过 formatMessage 处理，默认 false */
    errorTextFormatted?: boolean;
    /** 输入值格式校验正则，默认 /^\d+(\.\d{1,3})?$/ */
    regex?: RegExp;
    /** 空输入自定义错误提示文案 */
    emptyErrorText?: string;
    /** 格式错误自定义错误提示文案 */
    valueErrorText?: string;
    /** 返回按钮文案，支持 intl key，默认 'Back' */
    backText?: string;
    /** 埋点 key，用于筛选器确认时上报 */
    pointKey?: AllFilterPointType;
    /** 自定义埋点值提取函数，接收 rangeValue 返回埋点值 */
    getBackPoint?: (value: string) => string;
    /** 自定义类名 */
    className?:string;
  },
  ref: React.Ref<unknown> | undefined,
) {
  const { formatMessage } = useIntl();
  const optionsRef = useRef<Array<{ text: string; value: string }>>([
    ...options,
  ]);

  // 面板类型 select/customized
  const [activePanel, setActivePanel] = useState('select');
  // 动态新增自定义option
  const [rangeOptions, setRangeOptions] = useState<
    Array<{ text: string; value: string }>
  >([...options]);

  useEffect(() => {
    optionsRef.current = [...options];
    setRangeOptions([...options]);
  }, [options]);

  const handleCustomConfirm = (startRange: string, endRange: string) => {
    const [start, end] = getStartAndEndValue(startRange, endRange);

    const formatValue = (v: string | number) =>
      unitPosition === 'prefix' ? `${unit}${v}` : `${v}${unit}`;
    const newRange = {
      text: `${formatValue(start)} ~ ${formatValue(end)}`,
      value: `${start}-${end}`,
    };

    setRangeValue(newRange.value);
    // 动态新增自定义
    optionsRef.current = [...optionsRef.current, newRange];
    setRangeOptions([...optionsRef.current]);
    // 切换回下拉面板
    setActivePanel('select');
  };

  const getRangeButton = (value: string, isPoint: boolean = false) => {
    const rangeMap = arrayToSimpleObj(optionsRef.current);

    if (isPoint) return rangeMap[value];

    return rangeMap[value] === 'All'
      ? formatMessage({ id: 'All' })
      : rangeMap[value];
  };

  useImperativeHandle(ref, () => ({
    buttonElement: getRangeButton,
  }));

  return (
    <div className={classNames('follower-range-wrapper', className)}>
      <Dropdown
        className="dbo-followerfilter"
        button={
          <div className="dbo-followerfilter-inner">
            <span className="select-button-text">{placeholder[lang]}</span>
            <span className="dbo-followerfilter-value">
              {getRangeButton(rangeValue)}
            </span>
          </div>
        }
        size="m"
        appearance="button"
        matchButtonWidth
        clickClose={false}
        onClose={() => {
          try {
            let backValue = getRangeButton(rangeValue, true);
            if (getBackPoint) {
              backValue = getBackPoint(rangeValue);
            }
            addFilterBuriedPoint(pointKey, backValue);
          } catch (error) {}
        }}
      >
        {(close) => (
          <>
            {activePanel == 'select' ? (
              <div className="dbo-followerfilter-select-wrap">
                <List type="option" className="dbo-followerfilter-selectbox">
                  {rangeOptions?.map((item, index) => (
                    <List.Item
                      className={classNames({ 'is-selected': rangeValue == item.value })}
                      key={index}
                      onClick={() => setRangeValue(item.value)}
                    >
                      {item.text === 'All'
                        ? formatMessage({ id: 'All' })
                        : item.text}
                    </List.Item>
                  ))}
                </List>
                {hasCustomize && (
                  <div
                    className="dbo-followerfilter-footer"
                    onClick={() => {
                      setActivePanel('customized');
                    }}
                  >
                    {formatMessage({ id: 'Customize' })}
                  </div>
                )}
              </div>
            ) : (
              <CustomInput 
                unit={unit}
                unitPosition={unitPosition}
                maxValue={maxValue}
                minValue={minValue}
                maxErrorText={maxErrorText}
                minErrorText={minErrorText}
                errorTextFormatted={errorTextFormatted}
                regex={regex}
                emptyErrorText={emptyErrorText}
                valueErrorText={valueErrorText}
                backText={backText}
                rangeValue={rangeValue}
                onConfirm={handleCustomConfirm}
                onBack={() => setActivePanel('select')}
              />
            )}
          </>
        )}
      </Dropdown>
    </div>
  );
}

export default forwardRef(CustomFollowerRange);
